import React, { useState } from 'react'
import { trackEvent } from 'utils/analytics'
import PromotionLinkModal from './PromotionLinkModal'

/**
 * ShareButton
 * 品牌頁社群分享按鈕。
 * 行動裝置優先使用 Web Share API（原生分享）；
 * 桌面 fallback 到 LINE / Facebook / X / Threads / 複製連結 icon 按鈕。
 * 所有管道自動帶 UTM 追蹤參數。
 * owner/admin 點複製連結時彈出推廣 Modal。
 */
export default function ShareButton({ url, title, colors, translations = {}, utmContent = '', currentUser, teamId }) {
  const [copied, setCopied] = useState(false)
  const [promoModalOpen, setPromoModalOpen] = useState(false)
  const baseUrl = url || window.location.href
  const isOwnerOrAdmin = currentUser?.teamRole === 'owner' || currentUser?.teamRole === 'admin'
  const inviteCode = currentUser?.inviteCode || null
  const shareTitle = title || document.title

  const t = translations

  const withUtm = (medium) => {
    const sep = baseUrl.includes('?') ? '&' : '?'
    return `${baseUrl}${sep}utm_source=share&utm_medium=${medium}${utmContent ? `&utm_content=${encodeURIComponent(utmContent)}` : ''}`
  }

  // Web Share API — 優先在行動裝置上使用
  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: shareTitle,
        url: withUtm('native')
      })
      trackEvent('share_click', { channel: 'native' })
    } catch (err) {
      // 用戶取消分享或不支援，不需特別處理
    }
  }

  // LINE 分享
  const handleLineShare = () => {
    trackEvent('share_click', { channel: 'line' })
    const lineUrl = `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(withUtm('line'))}`
    window.open(lineUrl, '_blank', 'noopener,noreferrer')
  }

  // Facebook 分享
  const handleFacebookShare = () => {
    trackEvent('share_click', { channel: 'facebook' })
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(withUtm('facebook'))}`
    window.open(fbUrl, '_blank', 'noopener,noreferrer')
  }

  // X (Twitter) 分享
  const handleXShare = () => {
    trackEvent('share_click', { channel: 'x' })
    const xUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(withUtm('x'))}&text=${encodeURIComponent(shareTitle)}`
    window.open(xUrl, '_blank', 'noopener,noreferrer')
  }

  // Threads 分享
  const handleThreadsShare = () => {
    trackEvent('share_click', { channel: 'threads' })
    const text = `${shareTitle} ${withUtm('threads')}`
    const threadsUrl = `https://www.threads.net/intent/post?text=${encodeURIComponent(text)}`
    window.open(threadsUrl, '_blank', 'noopener,noreferrer')
  }

  // 複製連結 — owner/admin 彈推廣 Modal，其他人直接複製
  const handleCopyLink = async () => {
    if (isOwnerOrAdmin) {
      setPromoModalOpen(true)
      return
    }
    const copyUrl = withUtm('copy_link')
    try {
      await navigator.clipboard.writeText(copyUrl)
      trackEvent('share_click', { channel: 'copy_link' })
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = copyUrl
      textarea.style.cssText = 'position:fixed;opacity:0;'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const isMobile = typeof window !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  const supportsWebShare = isMobile && typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  // 行動裝置：顯示單一「分享」按鈕（Web Share API）
  if (supportsWebShare) {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={handleNativeShare}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer"
          style={{ backgroundColor: colors?.background || '#E6F0FF', color: colors?.primary || '#3366FF' }}
        >
          <ShareIcon />
          {t.share || 'Share'}
        </button>
      </div>
    )
  }

  // 桌面 fallback：icon-only 圓形按鈕
  return (
    <div className="flex items-center gap-1.5">
      <button onClick={handleLineShare} className="w-7 h-7 flex items-center justify-center rounded-full bg-[#06C755] text-white hover:opacity-80 transition-opacity cursor-pointer" title="LINE">
        <LineIcon />
      </button>
      <button onClick={handleFacebookShare} className="w-7 h-7 flex items-center justify-center rounded-full bg-[#1877F2] text-white hover:opacity-80 transition-opacity cursor-pointer" title="Facebook">
        <FacebookIcon />
      </button>
      <button onClick={handleXShare} className="w-7 h-7 flex items-center justify-center rounded-full bg-black text-white hover:opacity-80 transition-opacity cursor-pointer" title="X">
        <XIcon />
      </button>
      <button onClick={handleThreadsShare} className="w-7 h-7 flex items-center justify-center rounded-full bg-black text-white hover:opacity-80 transition-opacity cursor-pointer" title="Threads">
        <ThreadsIcon />
      </button>
      <button
        onClick={handleCopyLink}
        className={`w-7 h-7 flex items-center justify-center rounded-full transition-all cursor-pointer ${copied ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
        title={copied ? (t.link_copied || 'Copied!') : (t.copy_link || 'Copy Link')}
      >
        {copied ? <CheckIcon /> : <LinkIcon />}
      </button>
      <PromotionLinkModal
        isOpen={promoModalOpen}
        onClose={() => setPromoModalOpen(false)}
        baseUrl={baseUrl}
        teamId={teamId}
        inviteCode={inviteCode}
        colors={colors}
        translations={translations?.promotionModal}
      />
    </div>
  )
}

// SVG Icons

function ShareIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
    </svg>
  )
}

function LineIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
    </svg>
  )
}

function FacebookIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  )
}

function XIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}

function ThreadsIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.59 12c.025 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.17.408-2.265 1.33-3.084.899-.798 2.165-1.256 3.682-1.331.956-.048 1.856.04 2.688.257-.084-.749-.272-1.369-.558-1.842-.449-.746-1.164-1.136-2.13-1.16h-.036c-.794 0-1.73.27-2.437.738l-1.03-1.58c.949-.618 2.196-.988 3.467-.988h.053c1.54.038 2.733.635 3.55 1.773.7.974 1.084 2.29 1.14 3.912.424.2.822.43 1.19.693 1.193.856 2.048 2.07 2.474 3.508.501 1.696.382 3.78-1.227 5.568-1.798 2-4.18 2.898-7.27 2.917zM12.39 13.47c-1.04.052-1.862.353-2.378.862-.49.484-.696 1.078-.613 1.766.09.744.55 1.354 1.3 1.718.622.303 1.4.428 2.14.39 1.046-.056 1.879-.467 2.476-1.22.477-.6.795-1.39.943-2.351-.86-.29-1.82-.449-2.85-.399l-.018-.766z" />
    </svg>
  )
}

function LinkIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  )
}
